/** Figma DS 2026 "Semantic Colours", dark mode. Each path is the Figma name with "/" written as ".". */

import { primitiveColors } from "../primitives/colors";

export const action = {
  destructive: {
    /** Destructive action fill - Figma action/destructive/background → Red-500 #FF4314 */
    background: primitiveColors.red500,
    /** Icons on the destructive fill - Figma action/destructive/icon → Neutral/900 #0D1117 */
    icon: primitiveColors.neutral900,
    /** Text on the destructive fill - Figma action/destructive/text → Neutral/900 #0D1117 */
    text: primitiveColors.neutral900,
  },
  inverse: {
    /** White button fill - Figma action/inverse/background → Neutral/100 #E6EDF3 */
    background: primitiveColors.neutral100,
    "background-pressed": primitiveColors.white,
    /** Glyph on the white button - Figma action/inverse/icon → Neutral/900 #0D1117 */
    icon: primitiveColors.neutral900,
  },
  neutral: {
    /** Neutral button fill - Figma action/neutral/background → Neutral/975 #030F1A */
    background: primitiveColors.neutral975,
    /** Neutral button outline - Figma action/neutral/border → Neutral/700 #30363D */
    border: primitiveColors.neutral700,
    /** Neutral button glyph - Figma action/neutral/icon → Neutral/100 #E6EDF3 */
    icon: primitiveColors.neutral100,
    /** Neutral button label - Figma action/neutral/text → Neutral/100 #E6EDF3 */
    text: primitiveColors.neutral100,
  },
  primary: {
    /** Primary action fill (buttons, checkboxes, toggles) - Figma action/primary/background → Green-500 #31F613 */
    background: primitiveColors.green500,
    /** Icons on the primary fill - Figma action/primary/icon → Neutral/900 #0D1117 */
    icon: primitiveColors.neutral900,
    /** Dark text on the green primary / cur8Brand CTA - Figma action/primary/text → Neutral/900 #0D1117 */
    text: primitiveColors.neutral900,
  },
  secondary: {
    /** Secondary action fill - Figma action/secondary/background → Blue-600 #1E4FDD */
    background: primitiveColors.blue600,
    /** Dark blue tile that still reads as tappable (tool buttons) - Figma action/secondary/background-subtle → Blue-900 #0A1F4B */
    "background-subtle": primitiveColors.blue900,
    /** Secondary action border - Blue/Blue-400 #8CA1EE */
    border: primitiveColors.blue400,
    /** Icons on the secondary fill - Figma action/secondary/icon → White #FFFFFF */
    icon: primitiveColors.white,
    /** Text on the secondary fill - Figma action/secondary/text → Neutral/100 #E6EDF3 */
    text: primitiveColors.neutral100,
  },
  selected: {
    /** White fill of a selected radio or checkbox - Figma action/selected/background → Neutral/100 #E6EDF3 */
    background: primitiveColors.neutral100,
    /** Tick or dot on the selected fill - Figma action/selected/foreground → Neutral/975 #030F1A */
    foreground: primitiveColors.neutral975,
  },
} as const;

export const background = {
  "card-header": "#1E4FDD12",
  tint: "#1E4FDD1F",
  business: primitiveColors.extendedViolet950,
  /** Disabled surface - Figma dark/bg/disabled → Neutral-700 #30363D */
  disabled: primitiveColors.neutral700,
  /** Error alert/banner backgrounds - Figma background/error → Red-950 #4A1912 */
  error: primitiveColors.red950,
  /** Gain surfaces - attribute pills, rate lozenges, progress fills - Figma background/gain → Green-950 #0D2D19 */
  gain: primitiveColors.green950,
  /** Info background - Figma background/info → Blue-900 #0A1F4B */
  info: primitiveColors.blue900,
  /** Inset/recessed surface - Figma dark/bg/inset → Neutral/800 #1C2128 */
  inset: primitiveColors.neutral800,
  /** Light surfaces on dark pages - Neutral/100 #E6EDF3 (Figma "inverse-primary") */
  inverse: primitiveColors.neutral100,
  isa: primitiveColors.extendedJade950,
  /** Entity chip fill (opaque; never layer extra opacity) */
  neutral: primitiveColors.neutral825,
  /** App/screen background - Figma dark/bg/page → Neutral/975 #030F1A */
  page: primitiveColors.neutral975,
  personal: primitiveColors.extendedCyan950,
  /** Raised surfaces - Figma dark/bg/raised → Neutral-925 #09141C */
  raised: primitiveColors.neutral925,
  /** Success alert/banner backgrounds - Figma background/success → Green-950 #0D2D19 */
  success: primitiveColors.green950,
  "success-strong": primitiveColors.green500,
  /** Warning alert/banner backgrounds - Figma background/warning → Yellow-950 #8B5D00 */
  warning: primitiveColors.yellow950,
} as const;

export const border = {
  divider: "#8B949E14",
  accent: primitiveColors.green500,
  /** Standard borders - Neutral/700 #30363D */
  default: primitiveColors.neutral700,
  /** Disabled element borders - Figma border/disabled → Neutral/400 20% */
  disabled: primitiveColors.neutral400Alpha20,
  /** Error state borders - Red/Red-400 #FF6C47 */
  error: primitiveColors.red400,
  /** Focus rings, active states - Figma border/focus → Neutral/50 #F5F9FB */
  focus: primitiveColors.neutral50,
  inset: primitiveColors.neutral800,
  /** Info state borders - Figma border/link → Sky/Sky-400 #58A6FF */
  link: primitiveColors.sky400,
  /** Selected option outline - Figma border/selected → Sky/Sky-400 #58A6FF */
  selected: primitiveColors.sky400,
  /** Emphasised borders - Figma border/strong → Neutral/400 #8B949E */
  strong: primitiveColors.neutral400,
  /** Subtle dividers, separators - Figma border/subtle → Neutral/400 20% */
  subtle: primitiveColors.neutral400Alpha20,
  /** Success state borders - Figma border/success → Green-400 #82FF6F */
  success: primitiveColors.green400,
  /** Warning state borders - Figma dark/border/warning → Yellow/Yellow-300 #FFEF75 */
  warning: primitiveColors.yellow300,
} as const;

export const chart = {
  "data-1": primitiveColors.extendedIris100,
  "data-2": primitiveColors.extendedIris200,
  "data-3": primitiveColors.extendedIris300,
  "data-4": primitiveColors.extendedIris400,
  "data-5": primitiveColors.extendedIris500,
} as const;

export const icon = {
  accent: primitiveColors.green500,
  /** Brand-coloured icons - Figma icon/brand → Green-300 #AFFAA3 */
  brand: primitiveColors.green300,
  /** Business account icon - Figma icon/business → Extended/Violet #DD97F2 */
  business: primitiveColors.extendedViolet,
  /** Disabled icons - Figma icon/disabled → Neutral/450 #6E7781 */
  disabled: primitiveColors.neutral450,
  /** Error indicator icons - Red/Red-400 #FF6C47 */
  error: primitiveColors.red400,
  /** Gain icons - Figma icon/gain → Green-550 #44B234 */
  gain: primitiveColors.green550,
  /** IF-ISA icon - Figma icon/isa → Extended/Jade #3FA46A */
  isa: primitiveColors.extendedJade,
  /** Icons beside text that take the text colour - Figma icon/inline → Neutral/100 #E6EDF3 */
  inline: primitiveColors.neutral100,
  /** Informational icons - Figma dark/icon/info → Sky/Sky-400 #58A6FF */
  link: primitiveColors.sky400,
  /** Loss icons - Figma icon/loss → Red-300 #FF947A */
  loss: primitiveColors.red300,
  /** Personal account icon - Figma icon/personal → Extended/Cyan-Bright #22D3EE */
  personal: primitiveColors.extendedCyanBright,
  /** Default icons - White #FFFFFF */
  primary: primitiveColors.white,
  /** Icons on light backgrounds - Neutral/900 #0D1117 (inferred from text.inverse) */
  "primary-inverse": primitiveColors.neutral900,
  /** Subtle/supporting icons - Neutral/300 #C9D1D9 */
  secondary: primitiveColors.neutral300,
  /** Success indicator icons - Green/Green-400 #82FF6F */
  success: primitiveColors.green400,
  /** Quiet icons at tertiary text strength - Figma icon/tertiary → Neutral/400 #8B949E */
  tertiary: primitiveColors.neutral400,
  /** Warning indicator icons - Yellow/Yellow-300 #FFEF75 */
  warning: primitiveColors.yellow300,
} as const;

export const overlay = {
  /** Modal and sheet scrim - Figma overlay/overlay → Neutral/Black-70 */
  overlay: primitiveColors.overlayScrim70,
} as const;

export const text = {
  positive: primitiveColors.green700,
  accent: primitiveColors.green500,
  /** Business text on the business chip tint - Figma dark/product/entity/business-text → Extended/Violet #DD97F2 (base violet fails AA on the tint) */
  business: primitiveColors.extendedViolet,
  /** Disabled text - Figma text/disabled → Neutral/450 #6E7781 */
  disabled: primitiveColors.neutral450,
  /** Error messages, validation text - Red/Red-400 #FF6C47 */
  error: primitiveColors.red400,
  /** Profit, gains - Figma text/gain → Green-550 #44B234 */
  gain: primitiveColors.green550,
  /** IF-ISA accent - Figma text/isa → Extended/Jade #3FA46A */
  isa: primitiveColors.extendedJade,
  /** Informational text - Figma text/link → Sky/Sky-400 #58A6FF */
  link: primitiveColors.sky400,
  /** Loss, negative values - Figma text/loss → Red-300 #FF947A */
  loss: primitiveColors.red300,
  /** Personal account accent - Figma text/personal → Extended/Cyan-Bright #22D3EE */
  personal: primitiveColors.extendedCyanBright,
  /** Main content, headings, body - Neutral/100 #E6EDF3 */
  primary: primitiveColors.neutral100,
  /** Text on light backgrounds - Figma text/primary-inverse → Neutral/900 #0D1117 */
  "primary-inverse": primitiveColors.neutral900,
  /** Supporting text, descriptions - Neutral/300 #C9D1D9 */
  secondary: primitiveColors.neutral300,
  /** Supporting text on light backgrounds - Figma text/secondary-inverse → Neutral/500 #57606A */
  "secondary-inverse": primitiveColors.neutral500,
  /** Success messages, positive labels - Figma text/success → Green-400 #82FF6F */
  success: primitiveColors.green400,
  /** Captions, timestamps, metadata - Neutral/400 #8B949E */
  tertiary: primitiveColors.neutral400,
  /** Warning messages, caution labels - Yellow/Yellow-300 #FFEF75 */
  warning: primitiveColors.yellow300,
} as const;
